import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { IMatch } from '../pages/match/interfaces/match.interface';
import { RoleEnum } from '../enums/role.enum';
import { AuthService } from './auth.service';
import { isOpdrachtRoute, TAGGING_KOP } from '../helpers/tagging-opdracht';

export type OpdrachtStatus = 'todo' | 'tagging' | 'done';

/** Een wedstrijd die een taggingteam moet taggen (siq_arena, TaggingAssignment). */
export interface ITaggingAssignment {
  sid: string;
  /** Club-id van het taggingteam. */
  team: string;
  /** Gebruiker-sid van de tagger; leeg = nog aan niemand in het team. */
  tagger?: string;
  /** Club van de wedstrijd. */
  club: string;
  match: string;
  status: OpdrachtStatus;
  assignedAt?: string;
  startedAt?: string;
  doneAt?: string;
  note?: string;
}

export interface IMyAssignment {
  assignment: ITaggingAssignment;
  /** Leeg als de wedstrijd niet (meer) bestaat. */
  match: IMatch | null;
}

export interface IMyAssignments {
  items: IMyAssignment[];
  users?: { sid: string; firstName?: string; lastName?: string; email?: string }[];
  clubs?: { sid: string; name?: string }[];
}

/** De geopende opdracht, zodat hij een herlaadbeurt van de videospeler overleeft. */
const OPSLAG = 'tagging.actief';

export interface ActieveOpdracht {
  sid: string;
  match: string;
}

/**
 * Opdrachten van een taggingteam: "My matches" voor de tagger, "Team
 * matches" voor de beheerder van het team (docs/TAGGER-OPDRACHTEN.md).
 */
@Injectable({ providedIn: 'root' })
export class TaggingService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private readonly apiUrl = `${environment.apiUrl}/Tagging`;

  /**
   * De opdracht die de gebruiker geopend heeft. Zolang hij op de pagina's
   * van die wedstrijd of in de videospeler zit, gaat de kop mee
   * (interceptors/tagging.interceptor.ts); gaat hij ergens anders heen, dan
   * vervalt hij.
   */
  private actief: ActieveOpdracht | null = this.leesOpslag();

  constructor() {
    inject(Router)
      .events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        if (this.actief && !isOpdrachtRoute(e.urlAfterRedirects)) {
          this.zet(null);
        }
      });
  }

  private teamCache$: Observable<boolean> | null = null;

  private get rol(): string {
    const role: any = this.authService.user?.role;
    return typeof role === 'string' ? role : role?.key ?? '';
  }

  public isTagger(): boolean {
    return this.rol === RoleEnum.TAGGER;
  }

  public isBeheerder(): boolean {
    return this.rol === RoleEnum.CLUB_ADMIN;
  }

  /**
   * Is de eigen club een taggingteam? Een tagger zit altijd in een team; voor
   * de beheerder vraagt het de club op (kind "tagging"). Eén keer per sessie.
   */
  public isTaggingTeam(): Observable<boolean> {
    if (this.isTagger()) {
      return of(true);
    }
    if (!this.isBeheerder()) {
      return of(false);
    }
    this.teamCache$ ??= this.http.get<{ kind?: string }>(`${environment.authUrl}/Account/Club`).pipe(
      map((club) => club?.kind === 'tagging'),
      catchError(() => of(false)),
      shareReplay(1),
    );
    return this.teamCache$;
  }

  public mine(status?: OpdrachtStatus): Observable<IMyAssignments> {
    const params = status ? new HttpParams().set('status', status) : undefined;
    return this.http.get<IMyAssignments>(`${this.apiUrl}/Mine`, { params });
  }

  /** Status zetten; de beheerder zet ook de tagger ("-" = niemand). */
  public update(sid: string, data: { status?: OpdrachtStatus; tagger?: string }): Observable<ITaggingAssignment> {
    return this.http.patch<ITaggingAssignment>(`${this.apiUrl}/Mine/${sid}`, data);
  }

  /**
   * De eerste video van de wedstrijd van een opdracht, of null. De kop gaat
   * hier met de hand mee: de lijst Tagging is zelf geen opdrachtpagina.
   */
  public eersteVideo(opdracht: ActieveOpdracht): Observable<string | null> {
    const params = new HttpParams().set('match', opdracht.match).set('limit', '1');
    const headers = new HttpHeaders({ [TAGGING_KOP]: opdracht.sid });
    return this.http.get<{ result?: { items?: { sid: string }[] } }>(`${environment.apiUrl}/Videos`, { params, headers }).pipe(
      map((res) => res?.result?.items?.[0]?.sid ?? null),
      catchError(() => of(null)),
    );
  }

  /** Een opdracht openen: vanaf nu gaat de kop mee. */
  public open(opdracht: ActieveOpdracht): void {
    this.zet(opdracht);
  }

  /** De geopende opdracht, als de gebruiker nog op een pagina ervan is. */
  public opdracht(url: string): string | null {
    return this.actief && isOpdrachtRoute(url) ? this.actief.sid : null;
  }

  private zet(opdracht: ActieveOpdracht | null): void {
    this.actief = opdracht;
    try {
      if (opdracht) {
        sessionStorage.setItem(OPSLAG, JSON.stringify(opdracht));
      } else {
        sessionStorage.removeItem(OPSLAG);
      }
    } catch {
      // Zonder opslag werkt het ook; alleen niet na herladen.
    }
  }

  private leesOpslag(): ActieveOpdracht | null {
    try {
      const waarde = JSON.parse(sessionStorage.getItem(OPSLAG) ?? 'null');
      return waarde?.sid && waarde?.match ? { sid: String(waarde.sid), match: String(waarde.match) } : null;
    } catch {
      return null;
    }
  }
}
